const ATTR_COOKIE = "lk_utm";
const TAG_KEYS = ["ref", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const ATTR_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;
const SELF_HOST_KEYWORDS = ["link-ai.tech", "link-ai.cloud", "cowagent.ai"];
// [URL param appended by the ad platform, platform code expected by the backend]
const AD_CLICK_PARAMS: Array<[string, string]> = [
  ["bd_vid", "baidu"],
  ["msclkid", "bing"],
  ["gclid", "google"],
];
const AD_KEYS = ["ad_platform", "ad_click_id", "ad_click_url", "ad_click_ts"];

function readAttribution(): Record<string, unknown> | null {
  const entry = document.cookie.split("; ").find((c) => c.startsWith(`${ATTR_COOKIE}=`));
  if (!entry) return null;
  try {
    return JSON.parse(decodeURIComponent(entry.slice(ATTR_COOKIE.length + 1))) || {};
  } catch (e) {
    return {};
  }
}

function readAdClick(params: URLSearchParams): Record<string, unknown> | null {
  for (const [param, platform] of AD_CLICK_PARAMS) {
    const id = params.get(param);
    if (id) {
      // Long URLs are dropped rather than cut (the cookie must stay under 4KB);
      // the backend then rebuilds a URL carrying the click id.
      const href = window.location.href;
      return {
        ad_platform: platform,
        ad_click_id: id.slice(0, 255),
        ad_click_url: href.length <= 512 ? href : "",
        ad_click_ts: Date.now(),
      };
    }
  }
  return null;
}

/**
 * First-touch attribution cookie shared by portal / console / docs / Studio on
 * .link-ai.tech. The backend reads it at registration: ref / utm_source fill
 * sys_user.source when the page reported none, utm_* go to user_attribution.
 * - Written on the first visit that carries ref / utm_* or comes from an external site.
 * - Kept for 30 days and never overwritten, except that a tagged visit (ref / utm_*)
 *   replaces a touch that only had a referrer.
 * - A paid ad click id (bd_vid / msclkid / gclid) is kept as ad_* fields and replaced
 *   by every newer click, since ad platforms attribute to the latest click.
 * Must stay in sync with the portal-web, docs and Studio copies.
 */
export function captureAttribution(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const tags: Record<string, string> = {};
    for (const key of TAG_KEYS) {
      const value = params.get(key);
      if (value) tags[key] = value.slice(0, 128);
    }
    const tagged = Object.keys(tags).length > 0;
    const rawReferrer = document.referrer || "";
    const referrer = SELF_HOST_KEYWORDS.some((h) => rawReferrer.includes(h)) ? "" : rawReferrer;
    const adClick = readAdClick(params);

    const existing = readAttribution();
    let touch: Record<string, unknown>;
    if (existing && (!tagged || TAG_KEYS.some((k) => existing[k]))) {
      if (!adClick) return;
      touch = existing;
    } else if (!existing && !tagged && !referrer && !adClick) {
      return;
    } else {
      touch = {
        ...tags,
        landing_page: (window.location.origin + window.location.pathname).slice(0, 512),
        referrer: referrer.slice(0, 512),
        ts: Date.now(),
      };
      for (const key of AD_KEYS) {
        if (existing && existing[key] !== undefined) touch[key] = existing[key];
      }
    }

    const payload = { ...touch, ...(adClick || {}) };
    const host = window.location.hostname;
    const domain = host === "link-ai.tech" || host.endsWith(".link-ai.tech") ? "; domain=.link-ai.tech" : "";
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${ATTR_COOKIE}=${encodeURIComponent(JSON.stringify(payload))}` +
      `; max-age=${ATTR_MAX_AGE_SECONDS}; path=/${domain}; SameSite=Lax${secure}`;
  } catch (e) {
    // cookies unavailable: skip silently
  }
}
